import React, { useState } from 'react'
import {
  DEPOSIT_GROUP_COLOR,
  DEPOSIT_GROUP_LABEL,
  LEGEND_HINT,
  LEGEND_HINT_STORAGE_KEY,
  PAYOUT_KIND_COLOR,
  PAYOUT_KIND_LABEL,
} from '../core/consts'
import { BAR_HEIGHT, POINT_RADIUS } from '../core/layout'
import { DepositGroup, PayoutKind } from '../core/response.types'

type Props = {
  payoutKinds: PayoutKind[]
  depositGroups: DepositGroup[]
  hiddenKinds: Set<PayoutKind>
  hiddenGroups: Set<DepositGroup>
  onToggleKind: (k: PayoutKind) => void
  onToggleGroup: (g: DepositGroup) => void
}

const readHintSeen = (): boolean => {
  try {
    return window.localStorage.getItem(LEGEND_HINT_STORAGE_KEY) === '1'
  } catch {
    return true
  }
}

const markHintSeen = () => {
  try {
    window.localStorage.setItem(LEGEND_HINT_STORAGE_KEY, '1')
  } catch {
    // Недоступное хранилище — подсказка просто покажется снова.
  }
}

/** Пункт легенды — кнопка: скрытый пункт бледнеет и зачёркивается, слой уходит с графика. */
const LegendItem: React.FC<{
  hidden: boolean
  label: string
  onClick: () => void
  swatch: React.ReactNode
}> = ({ hidden, label, onClick, swatch }) => (
  <button
    type='button'
    className='btn btn-link p-0 d-inline-flex align-items-center gap-1 fs-8 text-muted'
    style={{
      opacity: hidden ? 0.45 : 1,
      textDecoration: hidden ? 'line-through' : 'underline dotted',
      textUnderlineOffset: 3,
      cursor: 'pointer',
    }}
    onClick={onClick}
    aria-pressed={!hidden}
    title={hidden ? 'Показать на графике' : 'Скрыть с графика'}
  >
    {swatch}
    {label}
  </button>
)

/** Маленькая живая демонстрация: пункт легенды сам нажимается и отжимается. */
const HintDemo: React.FC = () => (
  <span className='timeline-legend-demo d-inline-flex align-items-center gap-1 fs-8 px-2 py-1 rounded bg-light'>
    <style>{`
      @keyframes timelineLegendDemo { 0%, 45% { opacity: 1; text-decoration: underline dotted; }
        50%, 95% { opacity: .45; text-decoration: line-through; } 100% { opacity: 1; } }
      .timeline-legend-demo { animation: timelineLegendDemo 2.4s ease-in-out infinite; }
    `}</style>
    <svg width={18} height={BAR_HEIGHT}>
      <rect x={1} y={0} width={16} height={BAR_HEIGHT} rx={2} fill={PAYOUT_KIND_COLOR.paid} />
    </svg>
    {PAYOUT_KIND_LABEL.paid}
  </span>
)

const TimelineLegend: React.FC<Props> = ({
  payoutKinds,
  depositGroups,
  hiddenKinds,
  hiddenGroups,
  onToggleKind,
  onToggleGroup,
}) => {
  const [hintOpen, setHintOpen] = useState(() => !readHintSeen())
  const closeHint = () => {
    markHintSeen()
    setHintOpen(false)
  }

  return (
    <div className='mb-3'>
      {hintOpen && (
        <div className='d-flex flex-wrap align-items-center gap-3 mb-3 p-2 rounded border border-dashed border-primary fs-8'>
          <span className='badge badge-light-primary'>Новое</span>
          <span>{LEGEND_HINT}.</span>
          <HintDemo />
          <button
            type='button'
            className='btn btn-sm btn-light-primary py-1 ms-auto'
            onClick={closeHint}
          >
            Понятно
          </button>
        </div>
      )}
      <div className='d-flex flex-wrap align-items-center gap-4'>
        {payoutKinds.map((k) => (
          <LegendItem
            key={k}
            hidden={hiddenKinds.has(k)}
            label={PAYOUT_KIND_LABEL[k]}
            onClick={() => onToggleKind(k)}
            swatch={
              <svg width={18} height={BAR_HEIGHT}>
                <rect
                  x={1}
                  y={0}
                  width={16}
                  height={BAR_HEIGHT}
                  rx={2}
                  fill={PAYOUT_KIND_COLOR[k]}
                />
              </svg>
            }
          />
        ))}
        {depositGroups.map((g) => (
          <LegendItem
            key={g}
            hidden={hiddenGroups.has(g)}
            label={DEPOSIT_GROUP_LABEL[g]}
            onClick={() => onToggleGroup(g)}
            swatch={
              <svg width={10} height={10}>
                <circle cx={5} cy={5} r={POINT_RADIUS} fill={DEPOSIT_GROUP_COLOR[g]} />
              </svg>
            }
          />
        ))}
        <span
          className='badge badge-circle badge-light fs-8 fw-bold text-muted'
          style={{ width: 18, height: 18, cursor: 'help' }}
          title={LEGEND_HINT}
          aria-label={LEGEND_HINT}
        >
          ?
        </span>
      </div>
    </div>
  )
}

export default TimelineLegend
